import type { StateVector } from "../math/state_vector";
import { Vector3d } from "../math/vector3d";
import type { IPropulsionModel } from "./propulsion_model";
import { vacuumConditions } from "./propulsion_model";

/**
 * Complete spacecraft state at a point in time.
 */
export class SpacecraftState {
  id: string;
  name: string;

  /** Orbital state vector */
  state: StateVector;

  /** Dry mass in kg */
  dryMass: number;

  /** Current fuel mass in kg */
  fuelMass: number;

  /** Maximum fuel capacity in kg */
  fuelCapacity: number;

  /** Propulsion model */
  engine: IPropulsionModel | null;

  constructor(init: {
    id: string;
    name: string;
    state: StateVector;
    dryMass: number;
    fuelMass: number;
    fuelCapacity: number;
    engine: IPropulsionModel | null;
  }) {
    this.id = init.id;
    this.name = init.name;
    this.state = init.state;
    this.dryMass = init.dryMass;
    this.fuelMass = init.fuelMass;
    this.fuelCapacity = init.fuelCapacity;
    this.engine = init.engine;
  }

  get totalMass(): number {
    return this.dryMass + this.fuelMass;
  }

  get fuelFraction(): number {
    return this.fuelCapacity > 0 ? this.fuelMass / this.fuelCapacity : 0;
  }

  get massFraction(): number {
    return this.totalMass > 0 ? this.dryMass / this.totalMass : 1;
  }

  /** Available delta-V (m/s) with current fuel. */
  get availableDeltaV(): number {
    if (!this.engine || this.fuelMass <= 0 || this.dryMass <= 0) return 0;
    const ve = this.engine.exhaustVelocity;
    return ve * Math.log(this.totalMass / this.dryMass);
  }

  /** Maximum delta-V (m/s) if fully fueled. */
  get maxDeltaV(): number {
    if (!this.engine || this.fuelCapacity <= 0 || this.dryMass <= 0) return 0;
    const ve = this.engine.exhaustVelocity;
    return ve * Math.log((this.dryMass + this.fuelCapacity) / this.dryMass);
  }

  /** Maximum thrust (N). */
  get maxThrust(): number {
    return this.engine?.thrust(vacuumConditions()) ?? 0;
  }

  /** Maximum acceleration (m/s^2) at current mass. */
  get maxAcceleration(): number {
    return this.totalMass > 0 ? this.maxThrust / this.totalMass : 0;
  }

  /** Maximum acceleration in G. */
  get maxAccelerationG(): number {
    return this.maxAcceleration / 9.80665;
  }

  /**
   * Apply an impulsive delta-V. Modifies velocity and deducts fuel.
   * Returns actual delta-V applied in m/s (may be less if fuel runs out).
   *
   * @param deltaV Delta-V vector in km/s
   */
  applyImpulsiveBurn(deltaV: Vector3d): number {
    const dvMag = deltaV.magnitude * 1000.0; // Convert km/s to m/s for fuel calc
    if (dvMag <= 0 || !this.engine) return 0;

    const fuelNeeded = this.engine.fuelForDeltaV(dvMag, this.totalMass);

    let actualDvMag: number;
    if (fuelNeeded <= this.fuelMass) {
      // Enough fuel
      this.fuelMass -= fuelNeeded;
      actualDvMag = dvMag;
      this.state = {
        ...this.state,
        velocity: this.state.velocity.add(deltaV),
      };
    } else {
      // Partial burn: use all remaining fuel
      const massAfter = this.dryMass; // All fuel consumed
      const ve = this.engine.exhaustVelocity;
      actualDvMag = ve * Math.log(this.totalMass / massAfter);
      const actualDv = deltaV.normalized.scale(actualDvMag / 1000.0); // Back to km/s
      this.state = {
        ...this.state,
        velocity: this.state.velocity.add(actualDv),
      };
      this.fuelMass = 0;
    }

    return actualDvMag;
  }

  /** Create a deep copy of this state. */
  clone(): SpacecraftState {
    return new SpacecraftState({
      id: this.id,
      name: this.name,
      state: { ...this.state },
      dryMass: this.dryMass,
      fuelMass: this.fuelMass,
      fuelCapacity: this.fuelCapacity,
      engine: this.engine,
    });
  }
}
